import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import {
  ReactNode,
  createContext,
  useEffect,
  useState,
  useContext,
  useRef,
} from "react";
import { AuthContext } from "context/AuthContext";
import { WS_URL, API_URL, DB_STORE, ENV } from "lib/constants";
import { Signal } from "webrtc-player/common/signal";
import IndexDB from "lib/indexDB";
import { DbPicDataType, WsMessageDataType } from "lib/types";
import { dbUtil, idelPerformTasks } from "lib/utils";
import { errorHandler } from "lib/utils";
import { AppError } from "webrtc-player/common/app_error";
type SignalContextType = {
  signal: Signal;
  wsIsOpen: boolean;
  sessionId: string;
  wsMsgData: WsMessageDataType;
  db: dbUtil;
  getDb: () => Promise<dbUtil>;
};

const initialValue = {
  signal: null as any,
  wsIsOpen: false,
  sessionId: "",
  wsMsgData: null as any,
  db: null as any,
  getDb: () => null,
};

const dbSnapShotExpiredMap = new Map([
  [DB_STORE.EVENT_PIC, 3 * 24 * 60 * 60 * 1000],
  [DB_STORE.PRESET, 30 * 24 * 60 * 60 * 1000],
  [DB_STORE.SNAPSHOT, 30 * 24 * 60 * 60 * 1000],
  [DB_STORE.GOOGLE_DRIVE_CACHE, 1 * 24 * 60 * 60 * 1000],
]);

export const SignalContext = createContext<SignalContextType>(initialValue);
let dbWaiter = [];
export default function SignalProvider({ children }: { children?: ReactNode }) {
  const { user } = useContext(AuthContext);

  const [wsIsOpen, setWsIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [signal, setSignal] = useState(null as any);
  const [wsMsgData, setWsMsgData] = useState(null as WsMessageDataType);
  const [dbInstance, setDbInstance] = useState<dbUtil>(null as any);
  const dbInstanceRef = useRef<dbUtil>(null);
  const wsOnOpen = () => {
    console.log("wsOnOpen");
    setWsIsOpen(true);
  };
  const wsOnClose = () => {
    console.log("wsOnClose");
    setWsIsOpen(false);
  };
  const wsOnError = (error) => {
    console.log("wsOnError", error);
  };
  const wsOnMessage = (data: any) => {
    if (data?.error) {
      errorHandler(
        new AppError({
          name: "wsError",
          message: data?.error.message,
          code: data?.error.code,
        }),
      );
    }
    if (data?.result?.act) {
      console.log("wsOnMessage", data);
      setWsMsgData(data);
    }
  };
  useEffect(() => {
    if (dbInstance && dbWaiter.length) {
      dbWaiter.forEach((item) => item(dbInstance));
      dbWaiter = [];
    }
    dbInstanceRef.current = dbInstance;
  }, [dbInstance]);
  function getDb(): Promise<dbUtil> {
    return new Promise((resolve) => {
      if (dbInstanceRef.current) {
        resolve(dbInstanceRef.current);
      } else {
        dbWaiter.push(resolve);
      }
    });
  }
  useEffect(() => {
    const token = Cookies.get("token");
    const uuid = uuidv4();

    Signal.baseUrl = API_URL || "https://dev-api.agi7.ai";
    Signal.wsUrl = WS_URL || "wss://dev-ws.agi7.ai/agi7/api/user/ws";
    const sessionId = "user_" + user.id + ":" + uuid;
    const signal: Signal = new Signal({
      sessionId: sessionId,
      authToken: token!,
      wsOnOpen,
      wsOnClose,
      wsOnMessageExternal: wsOnMessage,
      wsOnError,
      debugging: Boolean(ENV === "development"),
    });
    setSessionId(sessionId);
    setSignal(signal);
    const myDb = new IndexDB();
    myDb.dbName = "agi7";
    myDb.dbVersion = 5;
    myDb.dbStores = [
      DB_STORE.PRESET,
      DB_STORE.SNAPSHOT,
      DB_STORE.EVENT_PIC,
      DB_STORE.GOOGLE_DRIVE_CACHE,
    ];
    myDb.openDB().then(() => {
      setDbInstance(new dbUtil(myDb));
    });
    return () => {
      dbInstance?.close();
    };
  }, []);

  useEffect(() => {
    if (!dbInstance) {
      return;
    }

    // delete indexeb expired data
    const prepareTask = async () => {
      for (let i = 0; i < Object.values(DB_STORE)?.length; i++) {
        const store = Object.values(DB_STORE)[i];
        const keys = await dbInstance?.getAllKeys(store);
        startTasks(store, keys);
      }
    };
    prepareTask();
  }, [dbInstance]);

  const startTasks = (store, keys) => {
    const tasks = [];
    const deleteTask = async (store, key) => {
      try {
        const res = await dbInstance?.read(store, key);
        const data = res as DbPicDataType;
        if (data?.__updateTime > Date.now() - dbSnapShotExpiredMap.get(store)) {
          return;
        }
        await dbInstance?.delete(store, key);
      } catch (error) {
        console.debug("deleteTask error", error);
      }
    };

    keys?.forEach((key) => {
      tasks.push({ fun: deleteTask, args: [store, key] });
    });
    idelPerformTasks(tasks);
  };

  return (
    <SignalContext.Provider
      value={{ signal, wsIsOpen, sessionId, wsMsgData, db: dbInstance, getDb }}
    >
      {children}
    </SignalContext.Provider>
  );
}
